import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { KeyRound, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { passwordResetApi } from '../lib/api';

/**
 * Reusable Change-Password dialog.
 * Props:
 *   - open:             boolean
 *   - onClose:          () => void
 *   - forced:           boolean — true when user MUST change (admin just reset them)
 *   - onSuccess:        (result) => void — called after API 200
 */
const ChangePasswordDialog = ({ open, onClose, forced = false, onSuccess }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { toast.error('Confirmation does not match.'); return; }
    if (next === current) { toast.error('New password must be different from the current one.'); return; }
    setBusy(true);
    try {
      const res = await passwordResetApi.changeOwn(current, next);
      toast.success('Password changed successfully. A confirmation email has been sent.');
      reset();
      if (onSuccess) onSuccess(res.data);
      if (onClose && !forced) onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !forced && onClose) onClose(); }}>
      <DialogContent className="max-w-md" aria-describedby="change-pwd-desc"
                     onPointerDownOutside={(e) => { if (forced) e.preventDefault(); }}
                     onEscapeKeyDown={(e) => { if (forced) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#C5A059]" />
            {forced ? 'Set your permanent password' : 'Change your password'}
          </DialogTitle>
          <DialogDescription id="change-pwd-desc">
            {forced
              ? 'Your administrator issued a temporary password. You must set a permanent password before you can continue.'
              : 'Enter your current password and pick a new one (minimum 8 characters).'}
          </DialogDescription>
        </DialogHeader>

        {forced && (
          <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Your <b>current password</b> is the temporary one your admin just sent you.
              After changing it, the temporary password will no longer work.
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">
              {forced ? 'Temporary password' : 'Current password'} *
            </Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                   autoFocus required data-testid="current-password-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">New password *</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                   required minLength={8} data-testid="new-password-input" />
            <p className="text-[10px] text-gray-400 mt-1">Minimum 8 characters.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Confirm new password *</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   required minLength={8} data-testid="confirm-password-input" />
          </div>

          <DialogFooter>
            {!forced && (
              <Button type="button" variant="outline" onClick={() => { reset(); onClose(); }}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={busy}
                    className="bg-[#1f1f1f] hover:bg-[#333333] text-white"
                    data-testid="submit-change-password-btn">
              {busy ? 'Saving…' : (forced ? 'Set permanent password' : 'Change password')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
